import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import { CloseOutlined } from "@ant-design/icons";
import { Image } from "antd";
import useAxios from "../../utils/useAxios";
import { FAULT_COLORS } from "../../utils/faults";
import { getLastAssetUpdate } from "../../features/Logistics/models/logisticsProgressPayload";

const InfoBox = ({ label, value, xs = 6 }) => (
  <Grid item xs={xs}>
    <Box
      sx={{
        p: 2,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "grey.50",
        height: "100%",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontSize: "0.65rem",
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
        {value || "—"}
      </Typography>
    </Box>
  </Grid>
);

export default function InstrumentInfo({ instrument, open, onClose }) {
  const api = useAxios();
  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastModifiedBy, setLastModifiedBy] = useState(null);

  const severityColor = FAULT_COLORS[instrument?.fault?.severity || "healthy"];

  const lastUpdate = useMemo(
    () =>
      getLastAssetUpdate({ ...instrument, last_modified_by: lastModifiedBy }),
    [instrument, lastModifiedBy],
  );

  // ── Nested serializer fields ─────────────────────────────────────────────
  const instrumentTypeName =
    instrument?.instrument_type_details?.name ?? null;

  const commProtocolName =
    instrument?.communication_protocol_details?.name ?? null;

  // ioPanel FK → TierSummarySerializer via panel_details
  const ioPanelName =
    instrument?.panel_details?.tagNumber ||
    instrument?.panel_details?.name ||
    instrument?.panel_details?.description ||
    null;

  // pid FK → EquipmentSummarySerializer via pid_details
  // instrument.pidNumber does not exist on the model; pid_details is the
  // nested representation added in the last serializer fix.
  const pidTagNumber =
    instrument?.pid_details?.tagNumber ||
    instrument?.pid_details?.name ||
    null;
  // ────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!instrument?.id) return;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [imagesRes, detailRes] = await Promise.all([
          api.get(`/intrument-images/?instrument=${instrument.id}`), // typo preserved to match your URL
          api.get(`/instruments/${instrument.id}/`),
        ]);
        const results = imagesRes.data?.results ?? imagesRes.data ?? [];
        setImageUrls(results.map((img) => img.image_url));
        setLastModifiedBy(detailRes.data?.last_modified_by || null);
      } catch (error) {
        console.error("Error fetching instrument images:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [instrument?.id]);

  const handleClose = () => {
    setImageUrls([]);
    setLastModifiedBy(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="instrument-info-modal-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", md: 720 },
          bgcolor: "background.paper",
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          borderRadius: "20px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5,
            bgcolor: "primary.main",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              id="instrument-info-modal-title"
              variant="h6"
              sx={{ fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}
            >
              {instrument?.tagNumber}
            </Typography>

            {instrumentTypeName && (
              <Chip
                label={instrumentTypeName}
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
              />
            )}

            {instrument?.fault?.severity &&
              instrument.fault.severity !== "healthy" && (
                <Chip
                  label={instrument.fault.severity.toUpperCase()}
                  size="small"
                  sx={{
                    bgcolor: severityColor,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.65rem",
                  }}
                />
              )}
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#fff",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseOutlined />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          sx={{
            overflowY: "auto",
            flexGrow: 1,
            px: 3,
            py: 2.5,
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "grey.400",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-track": { backgroundColor: "grey.100" },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Image Gallery */}
              <Box
                sx={{
                  width: "100%",
                  height: 220,
                  overflow: "hidden",
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "grey.50",
                }}
              >
                {imageUrls.length > 0 ? (
                  <Image.PreviewGroup
                    preview={{ zIndex: 2000 }}
                    items={imageUrls}
                  >
                    <Image
                      src={imageUrls[0]}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Image.PreviewGroup>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    No images available
                  </Typography>
                )}
              </Box>

              {/* Instrument Details */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  color: "primary.main",
                  mb: 1.5,
                }}
              >
                Instrument Details
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                <InfoBox
                  label="Tag Number"
                  value={instrument?.tagNumber}
                  xs={6}
                />
                <InfoBox
                  label="Instrument Type"
                  value={instrumentTypeName}
                  xs={6}
                />
                {/* ✅ Fixed: was instrument?.ioPanel (raw FK int) */}
                <InfoBox label="IO Panel" value={ioPanelName} xs={4} />
                <InfoBox label="Channel" value={instrument?.channel} xs={4} />
                <InfoBox label="Card" value={instrument?.card} xs={4} />
                {/* ✅ Fixed: was instrument?.pidNumber (field doesn't exist on model) */}
                <InfoBox label="P&ID" value={pidTagNumber} xs={6} />
                <InfoBox
                  label="Communication Protocol"
                  value={commProtocolName}
                  xs={6}
                />
                <InfoBox
                  label="Description"
                  value={instrument?.description}
                  xs={12}
                />
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {/* Traceability */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  color: "primary.main",
                  mb: 1.5,
                }}
              >
                Traceability
              </Typography>
              <Grid container spacing={1.5}>
                <InfoBox
                  label="Created"
                  value={instrument?.created}
                  xs={3}
                />
                <InfoBox
                  label="Last Updated"
                  value={lastUpdate?.at}
                  xs={3}
                />
                <InfoBox
                  label="Source"
                  value={lastUpdate?.sourceLabel}
                  xs={3}
                />
                <InfoBox
                  label="Created By"
                  value={instrument?.created_by}
                  xs={3}
                />
                <InfoBox
                  label="Updated By"
                  value={lastUpdate?.by}
                  xs={3}
                />
              </Grid>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
}